import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconSend} from '@tabler/icons-react';
import type {FC} from 'react';
import type {SesSuppressionResponse} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {IconSize} from 'src/design/atoms/icons/CommonIcons';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {ViewField} from 'src/design/atoms/ViewField';
import {formatUtcDateTime} from 'src/shared/utils/formatters';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import {SupportDetails} from './SupportDetails';

export interface EmailDeliveryCardProps {
  suppression?: SesSuppressionResponse;
  isLoading: boolean;
  errorMessage?: string;
  onRefresh: () => void;
  canRemoveSuppression: boolean;
  onRemoveSuppression: () => void;
  removing: boolean;
  disabled: boolean;
}

export const EmailDeliveryCard: FC<EmailDeliveryCardProps> = ({
  suppression: receivedSuppression,
  isLoading,
  errorMessage,
  onRefresh,
  canRemoveSuppression,
  onRemoveSuppression,
  removing,
  disabled,
}) => {
  const suppression =
    errorMessage || isLoading ? undefined : receivedSuppression;
  const suppressed = suppression?.isSuppressed === true;
  return (
    <SectionCard
      title="Email Delivery"
      icon={<IconSend size={IconSize.md} />}
      rightSection={
        isLoading ? (
          <Loader size="sm" />
        ) : suppression ? (
          suppressed ? (
            <Pill text="Suppressed" color="red" />
          ) : (
            <Pill text="Not suppressed" color="green" />
          )
        ) : (
          <Pill text="Unable to verify" color="gray" />
        )
      }>
      <Stack p="md" gap="md">
        {!isLoading && suppression && !suppressed && (
          <Text size="sm">
            No email suppression is recorded for this address. This does not
            confirm delivery.
          </Text>
        )}
        {!isLoading && !suppression && (
          <Alert
            color="red"
            variant="light"
            title="Email suppression status unavailable">
            {errorMessage || 'The suppression status could not be loaded.'}{' '}
            Refresh to check again.
          </Alert>
        )}
        {suppressed && (
          <>
            <Alert color="red" variant="light">
              Emails to this address bounced or were marked as spam. Proact EHS
              no longer sends to it.
            </Alert>
            <SimpleGrid cols={{base: 1, xs: 2}}>
              <ViewField
                label="Reason"
                value={suppression.reason || 'Unknown'}
              />
              <ViewField
                label="Since"
                value={formatUtcDateTime(suppression.suppressedAt)}
              />
            </SimpleGrid>
            <Text size="sm">
              Before removing suppression, verify the address and resolve the
              mailbox problem, or confirm that the user wants to receive email
              again after a spam complaint. Removing suppression allows sending
              to resume; it does not resend missed messages.
            </Text>
            {canRemoveSuppression && (
              <Box>
                <Button
                  variant="outline"
                  color="red"
                  onClick={onRemoveSuppression}
                  loading={removing}
                  disabled={disabled}>
                  Remove suppression
                </Button>
              </Box>
            )}
          </>
        )}
        <Text size="sm" c="dimmed">
          Notification email preferences are separate. Removing suppression does
          not turn notification emails on.
        </Text>
        <Box>
          <RefreshButton onClick={onRefresh} disabled={disabled || isLoading} />
        </Box>
        <SupportDetails title="Help with missing emails">
          <Text size="sm">
            Bounce means the receiving mail system rejected an email. Check for
            an incorrect address or a mailbox that cannot receive messages.
            Complaint means a recipient reported an email as spam.
          </Text>
          <Text size="sm">
            If there is no suppression, check the spam folder and ask the
            company IT administrator about mail filtering. For missing
            notifications, also check Notification Emails and the notification
            rules. Contact Proact support if messages are still missing.
          </Text>
        </SupportDetails>
      </Stack>
    </SectionCard>
  );
};
